import { useRef } from 'react';
import PropTypes from 'prop-types';
import isStorageAvailable from './isStorageAvailable';
import {
  forEachItemStartingWith,
  findKeyStartingWith,
  sortListByField,
} from './utils';

const useLocalStorage = ({ key, itemKey, index }) => {
  const indexKey = `index-${key}`;
  const storedIndex = window.localStorage.getItem(indexKey) || '0';
  const nextIndex = index || parseInt(storedIndex, 10) + 1;
  const nextKey = `${key}-${itemKey}-${nextIndex}`;
  const currentKeyValue = `${key}-${itemKey}`;
  const currentKey = useRef(findKeyStartingWith(currentKeyValue));

  const checkFreeSpace = (value, nextKeyIndex) =>
    isStorageAvailable('localStorage', value.concat(nextKeyIndex));

  const freeUpSpace = (value, allKeys) => {
    let existingStorageLength = 0;
    const currentStorageLength = (value || '').length;
    let logExistingKeys = [];

    allKeys.forEach(({ default: localKey }) => {
      existingStorageLength += (window.localStorage.getItem(localKey) || '')
        .length;
      logExistingKeys.push(localKey);

      if (existingStorageLength >= currentStorageLength) {
        logExistingKeys.forEach(window.localStorage.removeItem);
        saveItemToLocalStorage(nextKey, value);
        return true;
      }
    });
  };

  const collectStorageKeys = () => {
    let allKeys = [];
    forEachItemStartingWith(key, (localKey) => {
      allKeys.push({
        default: localKey,
        order: localKey.split('-').reverse().join('-'),
      });
    });
    return allKeys;
  };

  const saveItemToLocalStorage = (
    storageKey,
    storageValue,
    keyIndex,
    nextKeyIndex,
  ) => {
    storageKey && window.localStorage.setItem(storageKey, storageValue);
    keyIndex && window.localStorage.setItem(keyIndex, nextKeyIndex);
  };

  const updateItemToLocalStorage = (updateKey, updateValue) => {
    window.localStorage.setItem(updateKey, updateValue);
  };

  const clearItemFromLocalStorage = (localKey) => {
    window.localStorage.removeItem(localKey);
  };

  const saveItem = (value) => {
    const isFreeSpace = checkFreeSpace(value, nextIndex);

    if (isFreeSpace && currentKey.current) {
      updateItemToLocalStorage(currentKey.current, value);
    } else if (isFreeSpace && !currentKey.current) {
      saveItemToLocalStorage(nextKey, value);
      currentKey.current = findKeyStartingWith(currentKeyValue);
    } else {
      const allKeys = collectStorageKeys();
      const allKeysSortedByOrder = sortListByField(allKeys, 'order');
      freeUpSpace(value, allKeysSortedByOrder);
    }
  };

  const clearItem = () => {
    clearItemFromLocalStorage(currentKey.current);
  };

  const getItem = () =>
    window.localStorage.getItem(
      currentKey.current || findKeyStartingWith(currentKeyValue),
    );

  return [saveItem, clearItem, getItem];
};

useLocalStorage.propTypes = {
  key: PropTypes.string.isRequired,
  itemKey: PropTypes.string,
  index: PropTypes.number,
};

export default useLocalStorage;
